import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useGameStore } from '../stores/gameStore'
import { useSocket } from '../context/SocketContext'
import analytics from '../utils/analytics'

const HomePage = () => {
  const navigate = useNavigate()
  const [recoveryKey, setRecoveryKey] = useState('')
  const [showRecovery, setShowRecovery] = useState(false)
  const [showRecoveryInput, setShowRecoveryInput] = useState(false)
  const [globalStats, setGlobalStats] = useState(null)
  const [statsLoading, setStatsLoading] = useState(true)
  
  const {
    player,
    error,
    isLoading,
    explicitlyLeftLobby,
    setLoading,
    clearError,
    loadFromLocalStorage,
    resetPlayer,
    clearLocalStorage,
    setExplicitlyLeftLobby,
  } = useGameStore()
  
  const { 
    isConnected, 
    createIdentity, 
    recoverIdentity, 
    joinLobby 
  } = useSocket()

  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo({ top: 0, behavior: 'smooth' })
    
    // Try to load saved identity
    const savedData = loadFromLocalStorage()
    if (savedData) {
      setShowRecovery(true)
    }
    
    // Fetch global statistics
    fetchGlobalStats()
  }, [])

  const fetchGlobalStats = async () => {
    try {
      setStatsLoading(true)
      // In production, use window.location.origin. In local dev, use explicit backend URL
      const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== 'common-ground.local'
      const serverUrl = isProduction ? window.location.origin : (import.meta.env.VITE_SERVER_URL || 'http://common-ground.local:8000')
      const endpoint = isProduction ? '/api/stats/global' : '/stats/global'
      const response = await fetch(`${serverUrl}${endpoint}`)
      if (response.ok) {
        const stats = await response.json()
        setGlobalStats(stats)
      } else {
        console.error('Failed to fetch global stats')
      }
    } catch (error) {
      console.error('Error fetching global stats:', error)
    } finally {
      setStatsLoading(false)
    }
  }

  useEffect(() => {
    // Navigate to lobby if player has identity, but only if they didn't explicitly leave
    if (player.identity && !isLoading && !explicitlyLeftLobby) {
      navigate('/lobby')
    }
  }, [player.identity, isLoading, explicitlyLeftLobby, navigate])

  const handleCreateNewIdentity = () => {
    clearError()
    setLoading(true)
    setExplicitlyLeftLobby(false) // Clear flag since user wants to join lobby
    createIdentity()
    
    // Track identity creation intent
    analytics.trackGameEvent('identity_creation_started', {
      method: 'new_creation'
    })
  }

  const handleRecoverIdentity = () => {
    if (!recoveryKey.trim()) {
      return
    }
    clearError()
    setLoading(true)
    setExplicitlyLeftLobby(false) // Clear flag since user wants to join lobby
    recoverIdentity(recoveryKey.trim())
    
    // Track identity recovery attempt
    analytics.trackGameEvent('identity_recovery_started', {
      method: 'recovery_key',
      key_length: recoveryKey.trim().split('-').length
    })
  }

  const handleUseSavedIdentity = () => {
    if (player.recoveryKey) {
      setLoading(true)
      setExplicitlyLeftLobby(false) // Clear flag since user wants to join lobby
      recoverIdentity(player.recoveryKey)
    }
  }

  const handleResetAll = () => {
    console.log('Manually resetting all data')
    resetPlayer()
    clearLocalStorage()
    setShowRecovery(false)
    clearError()
  }

  const handleDownloadRecoveryKey = () => {
    if (!player.recoveryKey) {
      console.error('No recovery key available')
      return
    }

    // Track recovery key download
    analytics.recoveryKeyDownloaded(player.identity)

    // Create the content for the text file
    const content = `COMMON GROUND - Recovery Key Backup
=====================================

Your Recovery Key: ${player.recoveryKey}
Identity: ${player.identity}
Souls Helped: ${player.soulsHelped}
Generated: ${new Date().toLocaleString()}

IMPORTANT INSTRUCTIONS:
• Keep this recovery key safe and secure
• You need this word-based key to recover your identity
• Never share this key with anyone
• If you lose this key, you cannot recover your account

How to use:
1. Go to the Common Ground homepage
2. Click "Recover Existing Identity"
3. Enter your recovery key: ${player.recoveryKey}
4. Click "Recover Identity"

Your journey through purgatory continues...`

    // Create and download the file
    const blob = new Blob([content], { type: 'text/plain' })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `common-ground-recovery-${player.identity?.replace(/[^\w]/g, '')}-${player.recoveryKey}.txt`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Title */}
        <div className="text-center mb-12">
          <h1 
            className="text-6xl md:text-8xl font-bold mb-4 text-neon-green animate-pulse"
            style={{
              textShadow: '0 0 15px rgba(0, 255, 0, 0.4), 0 0 30px rgba(0, 255, 0, 0.2), 0 0 45px rgba(0, 255, 0, 0.1)',
              animation: 'subtle-glow 3s ease-in-out infinite alternate'
            }}
            data-text="COMMON GROUND"
          >
            COMMON GROUND
          </h1>
          
          
        </div>


        {/* Error display */}
        {error && (
          <div className="bg-purgatory-ember bg-opacity-20 border border-purgatory-ember p-4 rounded mb-6">
            <p className="text-purgatory-ember text-sm font-mono">{error}</p>
          </div>
        )}

        {/* Saved identity option */}
        {showRecovery && player.identity && (
          <div className="card-retro p-6 mb-6">
            <h3 className="text-lg font-bold text-neon-green mb-4 text-center">
              Continue Your Journey
            </h3>
            <div className="mb-4">
              <div className="text-4xl mb-4 text-center">{player.identity}</div>
              <button
                onClick={handleUseSavedIdentity}
                disabled={!isConnected || isLoading}
                className="btn-retro w-full"
              >
                Enter purgatory
              </button>
            </div>
          </div>
        )}

        {/* Begin Your Ascent - Hidden when user has saved identity */}
        {!(showRecovery && player.identity) && (
        <div className="card-retro p-6 mb-6">
          <h3 className="text-lg font-bold text-neon-green mb-4 text-center">
            Start your journey here.
          </h3>
          <p className="text-purgatory-mist text-sm mb-4 text-center">
            Create a new identity to begin your escape from purgatory.
          </p>
          
          {/* Recovery Input - shown when recover is clicked */}
          {showRecoveryInput ? (
            <div className="space-y-3">
              <input
                type="text"
                value={recoveryKey}
                onChange={(e) => setRecoveryKey(e.target.value)}
                placeholder="Enter recovery key (e.g., Magic-Forest-Dragon-Thunder)"
                className="input-retro w-full"
                maxLength={50}
                autoFocus
              />
              <div className="flex space-x-3">
                <button
                  onClick={handleRecoverIdentity}
                  disabled={!isConnected || isLoading || !recoveryKey.trim()}
                  className="btn-retro flex-1 border-neon-blue text-neon-blue hover:bg-neon-blue hover:text-purgatory-dark"
                >
                  Recover your soul
                </button>
                <button
                  onClick={() => {setShowRecoveryInput(false); setRecoveryKey('')}}
                  className="btn-retro border-purgatory-mist text-purgatory-mist hover:bg-purgatory-mist hover:text-purgatory-dark"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            /* Main CTA */
            <div>
              <button
                onClick={handleCreateNewIdentity}
                disabled={!isConnected || isLoading}
                className="w-full bg-neon-green text-purgatory-dark font-bold py-3 px-6 rounded border border-neon-green hover:bg-neon-green hover:bg-opacity-90 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {!isConnected ? 'Connecting you to the server' : 'Create a new soul'}
              </button>
            </div>
          )}
        </div>
        )}

        {/* Global Statistics */}
        <div className="mb-8">
          {statsLoading ? (
            <div className="text-center">
              <div className="animate-pulse flex justify-center space-x-8">
                <div className="h-16 w-24 bg-purgatory-ash rounded"></div>
                <div className="h-16 w-24 bg-purgatory-ash rounded"></div>
                <div className="h-16 w-24 bg-purgatory-ash rounded"></div>
                <div className="h-16 w-24 bg-purgatory-ash rounded"></div>
              </div>
            </div>
          ) : globalStats ? (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-center">
              <div className="card-retro p-4">
                <div className="text-3xl font-bold text-neon-blue mb-1">
                  {globalStats.waiting_to_escape || 0}
                </div>
                <div className="text-sm text-purgatory-mist">
                  In Purgatory
                </div>
              </div>
              <div className="card-retro p-4">
                <div className="text-3xl font-bold text-neon-green mb-1">
                  {globalStats.total_souls_helped.toLocaleString()}
                </div>
                <div className="text-sm text-purgatory-mist">
                  Souls Saved
                </div>
              </div>
              <div className="card-retro p-4">
                <div className="text-3xl font-bold text-purgatory-ember mb-1">
                  {globalStats.total_failed_escapes.toLocaleString()}
                </div>
                <div className="text-sm text-purgatory-mist">
                  Failed Escapes
                </div>
              </div>
              <div className="card-retro p-4">
                <div className="text-3xl font-bold text-purgatory-gold mb-1">
                  {globalStats.overall_common_ground_percentage}%
                </div>
                <div className="text-sm text-purgatory-mist">
                  Common Ground
                </div>
              </div>
            </div>
          ) : null}

        </div>

        {/* Recovery link */}
        <div className="text-center mt-8">
          <button
            onClick={() => setShowRecoveryInput(true)}
            className="text-neon-blue hover:text-neon-blue underline text-sm font-mono"
          >
            Click here to recover your lost soul.
          </button>
        </div>

      </div>
    </div>
  )
}

export default HomePage
